import React, { useState, useEffect } from "react";

const DebtToIncomeCalculator = () => {
  const [monthlyIncome, setMonthlyIncome] = useState(100000);
  const [homeLoanEMI, setHomeLoanEMI] = useState(25000);
  const [carLoanEMI, setCarLoanEMI] = useState(10000);
  const [personalLoanEMI, setPersonalLoanEMI] = useState(5000);
  const [creditCardPayment, setCreditCardPayment] = useState(3000);
  const [otherDebts, setOtherDebts] = useState(2000);
  const [result, setResult] = useState({
    totalDebt: "0.00",
    dtiRatio: "0.00",
    housingRatio: "0.00",
    availableIncome: "0.00",
    dtiCategory: "",
    loanEligibility: "",
    recommendation: "",
    maxRecommendedDebt: "0.00",
  });

  // DTI Calculation Function
  const calculateDTI = () => {
    const income = parseFloat(monthlyIncome);
    const home = parseFloat(homeLoanEMI);
    const car = parseFloat(carLoanEMI);
    const personal = parseFloat(personalLoanEMI);
    const credit = parseFloat(creditCardPayment);
    const other = parseFloat(otherDebts);

    if (isNaN(income) || income <= 0) return;

    const totalDebt = home + car + personal + credit + other;
    const dtiRatio = (totalDebt / income) * 100;
    const housingRatio = (home / income) * 100;
    const availableIncome = income - totalDebt;
    const maxRecommendedDebt = income * 0.40;

    let dtiCategory = "";
    let loanEligibility = "";
    let recommendation = "";

    if (dtiRatio <= 20) {
      dtiCategory = "Excellent";
      loanEligibility = "Highly Favorable";
      recommendation = "You have excellent debt management! You can easily qualify for new loans.";
    } else if (dtiRatio <= 35) {
      dtiCategory = "Good";
      loanEligibility = "Favorable";
      recommendation = "Your debt level is manageable. You should qualify for most loans.";
    } else if (dtiRatio <= 43) {
      dtiCategory = "Fair";
      loanEligibility = "Moderate";
      recommendation = "Your DTI is at the borderline. Consider reducing debt before new loans.";
    } else if (dtiRatio <= 50) {
      dtiCategory = "High";
      loanEligibility = "Difficult";
      recommendation = "Your debt is high. Focus on reducing obligations before taking new loans.";
    } else {
      dtiCategory = "Very High";
      loanEligibility = "Very Difficult";
      recommendation = "Your debt is concerning. Prioritize debt reduction immediately.";
    }

    setResult({
      totalDebt: totalDebt.toFixed(2),
      dtiRatio: dtiRatio.toFixed(2),
      housingRatio: housingRatio.toFixed(2),
      availableIncome: availableIncome.toFixed(2),
      dtiCategory,
      loanEligibility,
      recommendation,
      maxRecommendedDebt: maxRecommendedDebt.toFixed(2),
    });
  };

  useEffect(() => {
    calculateDTI();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
          </div>
          <h1>Debt-to-Income Ratio Calculator</h1>
          <p>
            Calculate your DTI ratio to understand your debt burden and loan eligibility
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Income & Debt Details
              </h2>

              {/* Monthly Income */}
              <div className="input-group">
                <label>Gross Monthly Income</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={monthlyIncome}
                    onChange={(e) => setMonthlyIncome(e.target.value)}
                    className="calc-input"
                    placeholder="100000"
                  />
                </div>
                <div className="quick-select-group">
                  {[50000, 100000, 150000, 200000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setMonthlyIncome(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{(amount / 1000).toFixed(0)}K
                    </button>
                  ))}
                </div>
              </div>

              {/* Home Loan EMI */}
              <div className="input-group">
                <label>Home Loan EMI</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={homeLoanEMI}
                    onChange={(e) => setHomeLoanEMI(e.target.value)}
                    className="calc-input"
                    placeholder="25000"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 15000, 25000, 35000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setHomeLoanEMI(amount)}
                      className="quick-select-btn secondary"
                    >
                      {amount === 0 ? "₹0" : `₹${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Car Loan EMI */}
              <div className="input-group">
                <label>Car Loan EMI</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={carLoanEMI}
                    onChange={(e) => setCarLoanEMI(e.target.value)}
                    className="calc-input"
                    placeholder="10000"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 8000, 12000, 18000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setCarLoanEMI(amount)}
                      className="quick-select-btn success"
                    >
                      {amount === 0 ? "₹0" : `₹${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Personal Loan EMI */}
              <div className="input-group">
                <label>Personal Loan EMI</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={personalLoanEMI}
                    onChange={(e) => setPersonalLoanEMI(e.target.value)}
                    className="calc-input"
                    placeholder="5000"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 3000, 5000, 10000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setPersonalLoanEMI(amount)}
                      className="quick-select-btn secondary"
                    >
                      {amount === 0 ? "₹0" : `₹${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Credit Card Payment */}
              <div className="input-group">
                <label>Credit Card Payments</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={creditCardPayment}
                    onChange={(e) => setCreditCardPayment(e.target.value)}
                    className="calc-input"
                    placeholder="3000"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 2000, 5000, 8000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setCreditCardPayment(amount)}
                      className="quick-select-btn success"
                    >
                      {amount === 0 ? "₹0" : `₹${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Other Debts */}
              <div className="input-group">
                <label>Other Monthly Debt Payments</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={otherDebts}
                    onChange={(e) => setOtherDebts(e.target.value)}
                    className="calc-input"
                    placeholder="2000"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 1000, 3000, 5000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setOtherDebts(amount)}
                      className="quick-select-btn secondary"
                    >
                      {amount === 0 ? "₹0" : `₹${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateDTI} className="calculate-btn">
                Calculate DTI Ratio
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                DTI Analysis
              </h2>

              <div className="result-card">
                {/* DTI Ratio - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Your DTI Ratio</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.dtiRatio}%
                  </div>
                  <div className="result-subtext">
                    {result.dtiCategory} - {result.loanEligibility}
                  </div>
                </div>

                {/* Total Monthly Debt */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Monthly Debt</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalDebt).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    All debt obligations
                  </div>
                </div>

                {/* Available Income */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Available Income</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.availableIncome).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    After debt payments
                  </div>
                </div>

                {/* Housing Ratio */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Housing Ratio</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.housingRatio}%
                  </div>
                  <div className="result-subtext">
                    Home loan vs income
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Income Distribution</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Debt Payments</span>
                      <span className="breakdown-percentage">
                        {result.dtiRatio}% (₹{Number(result.totalDebt).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${Math.min(parseFloat(result.dtiRatio), 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Available Income</span>
                      <span className="breakdown-percentage">
                        {(100 - parseFloat(result.dtiRatio)).toFixed(2)}% (₹{Number(result.availableIncome).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${Math.max(100 - parseFloat(result.dtiRatio), 0)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Recommendation Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #3b82f6'}}>
                  <h3 className="breakdown-title" style={{color: '#1e40af', marginBottom: '0.5rem'}}>Recommendation</h3>
                  <p style={{fontSize: '0.875rem', color: '#1e3a8a', lineHeight: '1.5', margin: 0}}>
                    {result.recommendation}
                  </p>
                  <p style={{fontSize: '0.875rem', color: '#1e3a8a', lineHeight: '1.5', margin: '0.5rem 0 0 0'}}>
                    <strong>Max Recommended Debt:</strong> ₹{Number(result.maxRecommendedDebt).toLocaleString("en-IN")} (40% of income)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>What is Debt-to-Income Ratio?</h3>
              <p>
                Debt-to-Income (DTI) ratio is a key financial metric that measures the percentage of your gross monthly 
                income that goes toward paying debts. Lenders use DTI to assess your ability to manage monthly payments 
                and repay borrowed money. A lower DTI indicates better financial health and makes you more attractive to 
                lenders. In India, most banks prefer a DTI ratio below 40-45% for home loans and 50% for other loans. 
                The ratio includes all recurring monthly debt obligations like home loan EMIs, car loans, personal loans, 
                credit card payments, and other debt commitments. It doesn't include utilities, groceries, insurance, or 
                other non-debt expenses. Maintaining a healthy DTI ratio ensures you have sufficient income for living 
                expenses while meeting debt obligations comfortably.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>DTI Ratio Formula:</h4>
            <div className="formula-display">
              DTI Ratio = (Total Monthly Debt Payments ÷ Gross Monthly Income) × 100
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Gross Monthly Income</div>
                <div className="formula-param-value">Total income before taxes and deductions</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Monthly Debt Payments</div>
                <div className="formula-param-value">All recurring loan EMIs and obligations</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Housing Ratio</div>
                <div className="formula-param-value">Housing costs as % of income (ideally under 28%)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Back-End Ratio</div>
                <div className="formula-param-value">All debts as % of income (total DTI)</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is a good debt-to-income ratio in India?</h5>
              <p>
                In India, a DTI ratio below 20% is considered excellent, showing strong financial health with minimal 
                debt burden. A ratio between 20-35% is good and acceptable for most loan applications. Between 35-43% 
                is fair but approaching the upper limit that most lenders prefer. Above 43% is considered high risk, 
                and many banks may reject loan applications or offer less favorable terms. For home loans specifically, 
                banks typically prefer DTI ratios below 40%, though some may accept up to 50% for high-income borrowers 
                with strong credit profiles. RBI guidelines and individual bank policies influence these thresholds. 
                Remember that a lower DTI not only improves loan eligibility but also ensures you have adequate funds 
                for emergencies and other expenses beyond debt obligations.
              </p>
            </div>

            <div className="faq-item">
              <h5>What debts should I include in DTI calculation?</h5>
              <p>
                Include all recurring monthly debt obligations in your DTI calculation: home loan or rent EMIs, car 
                loan EMIs, personal loan EMIs, education loan EMIs, credit card minimum payments or average monthly 
                payments if you carry balances, gold loan EMIs, business loan EMIs if personally guaranteed, co-signed 
                loan obligations where you're liable, and any other installment debt. DO NOT include utilities (electricity, 
                water), groceries and food expenses, insurance premiums (unless financed), medical expenses, transportation 
                costs (fuel, parking), entertainment and lifestyle expenses, savings and investments, or one-time expenses. 
                For credit cards, use either minimum payment due or average monthly payment over 6 months. Lenders verify 
                these through CIBIL reports and bank statements, so accurate reporting is essential.
              </p>
            </div>

            <div className="faq-item">
              <h5>How can I improve my debt-to-income ratio?</h5>
              <p>
                Improve your DTI through two approaches: increase income or reduce debt. To increase income: negotiate 
                salary raises, take on additional freelance or consulting work, start a side business, rent out property, 
                or monetize skills through part-time work. To reduce debt: make extra payments toward loans to pay them 
                off faster, consolidate high-interest debts into lower-interest loans, avoid taking new loans or credit 
                card debt, pay off smallest debts first for quick wins (debt snowball method), refinance loans at lower 
                interest rates to reduce EMIs, close unused credit cards to eliminate temptation, increase EMI amounts 
                to shorten tenure and reduce total interest, and avoid co-signing loans. Focus on high-interest debts 
                first. Even a 5% reduction in DTI significantly improves loan eligibility and terms.
              </p>
            </div>

            <div className="faq-item">
              <h5>Does DTI ratio affect my credit score?</h5>
              <p>
                DTI ratio doesn't directly impact your CIBIL score as credit bureaus don't track your income. However, 
                the factors that create high DTI often hurt credit scores indirectly. High debt balances increase credit 
                utilization (debt vs. available credit), which is a major credit score factor. Missing payments due to 
                high debt burden severely damages scores. Multiple loan applications when DTI is high lead to hard 
                inquiries that temporarily lower scores. Maxing out credit limits signals financial stress. When lenders 
                evaluate loan applications, they consider both credit score and DTI ratio independently. You can have a 
                high credit score but poor DTI (or vice versa). Both need to be healthy for loan approval. Managing 
                debts responsibly improves both metrics simultaneously - making timely payments boosts credit score while 
                reducing balances improves DTI ratio.
              </p>
            </div>

            <div className="faq-item">
              <h5>What's the difference between front-end and back-end DTI ratio?</h5>
              <p>
                Front-end DTI ratio (also called housing ratio) measures only housing-related expenses as a percentage 
                of income. In India, this typically includes home loan EMI, property taxes, and maintenance charges. 
                Lenders prefer this below 28-30%. It shows whether you can afford housing costs specifically. Back-end 
                DTI ratio (total DTI) includes all monthly debt obligations - home loan, car loans, personal loans, 
                credit cards, and other debts. This is the comprehensive measure lenders emphasize, ideally below 40-43%. 
                For example: if income is ₹1,00,000, home loan EMI is ₹25,000 (25% front-end ratio), and total debts 
                are ₹40,000 (40% back-end ratio), both ratios matter. Some borrowers have manageable housing costs but 
                excessive other debts, or vice versa. Both ratios must be within acceptable limits for loan approval.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebtToIncomeCalculator;